import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { 
  CheckCircle2, 
  TrendingUp, 
  Target, 
  BarChart3,
  Brain,
  Users,
  ArrowRight,
  Sparkles,
  Shield,
  Zap
} from 'lucide-react';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_player-connect-ikons/artifacts/bp71r7p3_IMG_9412.png';

function LandingPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <img src={LOGO_URL} alt="Ikons" className="h-10 md:h-12" />
          <Button 
            onClick={() => navigate('/login')}
            className="bg-[#D4AF37] hover:bg-[#A67C00] text-white rounded-full px-6"
          >
            Acceder
          </Button>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-4xl mx-auto text-center">
          <div className="mb-8">
            <span className="inline-block px-4 py-2 bg-[#D4AF37]/10 text-[#A67C00] text-sm font-semibold rounded-full uppercase tracking-wider">
              Ikons Player
            </span>
          </div>
          
          <h1 className="text-4xl md:text-6xl font-bold text-[#4F4F4F] mb-6 leading-tight">
            Tu carrera no se deja al azar.
          </h1>
          
          <p className="text-xl md:text-2xl text-[#808080] mb-4 leading-relaxed">
            Se construye con decisiones, seguimiento y compromiso.
          </p>
          <p className="text-xl md:text-2xl text-[#4F4F4F] font-medium mb-12">
            Ikons Player es donde todo eso sucede.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button 
              size="lg"
              onClick={() => navigate('/login')}
              className="bg-[#D4AF37] hover:bg-[#A67C00] text-white rounded-full px-8 py-6 text-lg shadow-lg shadow-[#D4AF37]/25"
              data-testid="hero-cta-login"
            >
              Acceder a mi panel
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <Button 
              size="lg"
              variant="outline"
              onClick={() => document.getElementById('problema').scrollIntoView({ behavior: 'smooth' })}
              className="border-[#4F4F4F] text-[#4F4F4F] hover:bg-[#4F4F4F] hover:text-white rounded-full px-8 py-6 text-lg"
            >
              Conocer más
            </Button>
          </div>
        </div>
      </section>

      {/* Problema Section */}
      <section id="problema" className="py-24 px-6 bg-[#4F4F4F]">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-12 text-center">
            La mayoría de los jugadores entrena…<br />
            <span className="text-[#D4AF37]">pero no gestiona su carrera.</span>
          </h2>
          
          <div className="grid md:grid-cols-2 gap-6">
            {[
              'No saben en qué punto están realmente',
              'No tienen claridad sobre qué mejorar',
              'No reciben seguimiento personalizado',
              'Dependen de sensaciones, no de datos'
            ].map((item, index) => (
              <div 
                key={index}
                className="flex items-start gap-4 p-6 bg-white/5 rounded-xl border border-white/10"
              >
                <div className="w-8 h-8 rounded-full bg-[#D4AF37]/20 flex items-center justify-center flex-shrink-0">
                  <span className="text-[#D4AF37] font-bold">{index + 1}</span>
                </div>
                <p className="text-white/90 text-lg">{item}</p>
              </div>
            ))}
          </div>
          
          <p className="text-center text-2xl text-[#D4AF37] font-medium mt-12">
            Y ahí es donde muchos se estancan.
          </p>
        </div>
      </section>

      {/* Solución Section */}
      <section className="py-24 px-6 bg-white">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-[#4F4F4F] mb-6 text-center">
            Ikons Player transforma tu desarrollo<br />
            <span className="text-[#D4AF37]">en un proceso estructurado.</span>
          </h2>
          
          <p className="text-xl text-[#808080] text-center mb-12">Un espacio donde:</p>
          
          <div className="grid md:grid-cols-2 gap-8">
            {[
              { id: 'evolution', icon: BarChart3, text: 'Registras tu evolución mes a mes' },
              { id: 'awareness', icon: Target, text: 'Tomas conciencia de tu rendimiento real' },
              { id: 'tools', icon: Sparkles, text: 'Recibes herramientas adaptadas a ti' },
              { id: 'direction', icon: TrendingUp, text: 'Trabajas con dirección, no al azar' }
            ].map((item) => (
              <div 
                key={item.id}
                className="flex items-center gap-4 p-6 bg-gray-50 rounded-xl hover:bg-[#D4AF37]/5 transition-colors"
              >
                <div className="w-12 h-12 rounded-full bg-[#D4AF37] flex items-center justify-center flex-shrink-0">
                  <item.icon className="h-6 w-6 text-white" />
                </div>
                <p className="text-[#4F4F4F] text-lg font-medium">{item.text}</p>
              </div>
            ))}
          </div>
          
          <div className="mt-16 text-center">
            <p className="text-xl text-[#808080]">Esto no es control.</p>
            <p className="text-3xl font-bold text-[#D4AF37] mt-2">Es claridad.</p>
          </div>
        </div>
      </section>

      {/* Sistema Section */}
      <section className="py-24 px-6 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-[#4F4F4F] mb-4 text-center">
            Un sistema simple.
          </h2>
          <p className="text-3xl md:text-4xl font-bold text-[#D4AF37] mb-16 text-center">
            Un impacto real.
          </p>
          
          <div className="grid md:grid-cols-4 gap-6">
            {[
              {
                step: '01',
                title: 'CHECK-IN MENSUAL',
                description: 'Respondes preguntas clave sobre tu rendimiento, sensaciones y contexto.'
              },
              {
                step: '02',
                title: 'ANÁLISIS',
                description: 'Detectamos patrones, puntos de mejora y oportunidades.'
              },
              {
                step: '03',
                title: 'ACCIÓN',
                description: 'Recibes herramientas y enfoque específico para tu momento.'
              },
              {
                step: '04',
                title: 'SEGUIMIENTO',
                description: 'Medimos tu evolución y ajustamos el proceso.'
              }
            ].map((item) => (
              <div 
                key={item.step}
                className="relative p-6 bg-white rounded-2xl shadow-lg border border-gray-100 hover:border-[#D4AF37]/50 transition-all hover:-translate-y-1"
              >
                <span className="text-5xl font-bold text-[#D4AF37]/20">{item.step}</span>
                <h3 className="text-lg font-bold text-[#4F4F4F] mt-4 mb-3">{item.title}</h3>
                <p className="text-[#808080] text-sm leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Beneficios Section */}
      <section className="py-24 px-6 bg-[#4F4F4F]">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4 text-center">
            Esto es lo que cambia cuando<br />
            <span className="text-[#D4AF37]">te tomas en serio tu proceso:</span>
          </h2>
          
          <div className="grid md:grid-cols-2 gap-4 mt-12">
            {[
              'Mayor claridad sobre tu nivel real',
              'Mejor toma de decisiones',
              'Evolución constante y medible',
              'Más confianza dentro y fuera del campo',
              'Relación más cercana y estratégica con la agencia'
            ].map((item, index) => (
              <div 
                key={index}
                className="flex items-center gap-4 p-5 bg-white/5 rounded-xl"
              >
                <CheckCircle2 className="h-6 w-6 text-[#D4AF37] flex-shrink-0" />
                <p className="text-white text-lg">{item}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Personalización Section */}
      <section className="py-24 px-6 bg-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-[#4F4F4F] mb-6">
            No trabajamos con todos los jugadores igual.
          </h2>
          
          <p className="text-xl text-[#808080] mb-12">Trabajamos contigo en base a:</p>
          
          <div className="grid md:grid-cols-4 gap-6">
            {[
              { icon: Target, text: 'Tu momento de carrera' },
              { icon: Users, text: 'Tu contexto competitivo' },
              { icon: TrendingUp, text: 'Tus objetivos reales' },
              { icon: Brain, text: 'Tu mentalidad' }
            ].map((item, index) => (
              <div 
                key={index}
                className="p-6 bg-gray-50 rounded-xl"
              >
                <div className="w-14 h-14 rounded-full bg-[#D4AF37]/10 flex items-center justify-center mx-auto mb-4">
                  <item.icon className="h-7 w-7 text-[#D4AF37]" />
                </div>
                <p className="text-[#4F4F4F] font-medium">{item.text}</p>
              </div>
            ))}
          </div>
          
          <div className="mt-12 p-8 bg-gradient-to-r from-[#D4AF37]/10 via-[#D4AF37]/5 to-[#D4AF37]/10 rounded-2xl">
            <p className="text-xl text-[#4F4F4F]">Porque no eres uno más.</p>
            <p className="text-2xl font-bold text-[#D4AF37] mt-2">Y tu camino tampoco debería serlo.</p>
          </div>
        </div>
      </section>

      {/* Exclusividad Section */}
      <section className="py-24 px-6 bg-gray-50">
        <div className="max-w-3xl mx-auto text-center">
          <div className="inline-block p-4 bg-[#D4AF37]/10 rounded-full mb-8">
            <Shield className="h-10 w-10 text-[#D4AF37]" />
          </div>
          
          <h2 className="text-3xl md:text-4xl font-bold text-[#4F4F4F] mb-6">
            Esto no es para todos.
          </h2>
          
          <p className="text-xl text-[#808080] mb-8">Ikons Player es para jugadores que:</p>
          
          <div className="space-y-4 text-left max-w-md mx-auto">
            {[
              'Quieren crecer de verdad',
              'Están dispuestos a responsabilizarse',
              'Entienden que el detalle marca la diferencia'
            ].map((item, index) => (
              <div 
                key={index}
                className="flex items-center gap-4 p-4 bg-white rounded-xl shadow-sm"
              >
                <Zap className="h-5 w-5 text-[#D4AF37] flex-shrink-0" />
                <p className="text-[#4F4F4F] font-medium">{item}</p>
              </div>
            ))}
          </div>
          
          <div className="mt-12 p-6 bg-[#4F4F4F] rounded-2xl text-white">
            <p className="text-lg">Si estás dentro, es porque creemos en tu potencial.</p>
            <p className="text-xl font-bold text-[#D4AF37] mt-2">Ahora toca demostrarlo.</p>
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="py-24 px-6 bg-gradient-to-b from-[#4F4F4F] to-[#3a3a3a]">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl md:text-5xl font-bold text-white mb-8">
            Tu desarrollo empieza<br />
            <span className="text-[#D4AF37]">con una decisión.</span>
          </h2>
          
          <Button 
            size="lg"
            onClick={() => navigate('/login')}
            className="bg-[#D4AF37] hover:bg-[#A67C00] text-white rounded-full px-12 py-7 text-xl shadow-2xl shadow-[#D4AF37]/30 hover:scale-105 transition-transform"
            data-testid="final-cta-login"
          >
            Acceder a mi panel
            <ArrowRight className="ml-3 h-6 w-6" />
          </Button>
          
          <div className="mt-16">
            <img src={LOGO_URL} alt="Ikons" className="h-12 mx-auto opacity-80" />
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 bg-[#2a2a2a] text-center">
        <p className="text-[#808080] text-sm">
          © {new Date().getFullYear()} Ikons by Solocracks. Todos los derechos reservados.
        </p>
      </footer>
    </div>
  );
}

export default LandingPage;
